// Hinweisbalken des Support-Zugangs (docs/anmeldung/SPEZIFIKATION.md §18): Solange der Betreiber über den Support-Zugang
// in einem fremden Betrieb arbeitet, steht unter der Topbar – deutlich, nicht ausblendbar – „Support-Ansicht: Sie arbeiten
// im Betrieb X als Betreiber“, dazu der Weg zurück (eigener Betrieb) und „Abmelden“. Quelle ist allein
// `session.support.active` (GET /api/session); alles, was der Betreiber hier tut, steht im Audit als Support.
import { Headset, LogOut, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toastError } from '../api/errors'
import { useSession } from '../api/session'
import type { SessionMembership } from '../api/types'
import { cn } from '../lib/cn'
import { selectTenant } from '../stores/session'
import { TONE_SUBTLE } from '../ui/Badge'
import { Button } from '../ui/Button'
import { DropdownMenu } from '../ui/DropdownMenu'
import { signOut } from './signOut'

/** Überschrift des Balkens – auch für Tests */
export const supportBannerTitle = (tenantName: string): string =>
  `Support-Ansicht: Sie arbeiten im Betrieb ${tenantName} als Betreiber.`

function BackToOwn({
  memberships,
  onSelect,
  busy,
}: {
  memberships: readonly SessionMembership[]
  onSelect: (tenantId: string) => void
  busy: boolean
}) {
  const first = memberships[0]
  if (!first) return null
  if (memberships.length === 1) {
    return (
      <Button
        size="sm"
        variant="secondary"
        icon={Undo2}
        loading={busy}
        onClick={() => onSelect(first.tenantId)}
        data-testid="support-banner-back"
      >
        Zurück zu {first.tenantName}
      </Button>
    )
  }
  return (
    <DropdownMenu
      align="end"
      width="sm"
      trigger={
        <Button size="sm" variant="secondary" icon={Undo2} loading={busy} data-testid="support-banner-back">
          Zum eigenen Betrieb
        </Button>
      }
      items={[
        { type: 'label', id: 'own', label: 'Eigener Betrieb' },
        ...memberships.map((membership) => ({
          id: membership.tenantId,
          label: membership.tenantName,
          onSelect: () => onSelect(membership.tenantId),
        })),
      ]}
    />
  )
}

export function SupportBanner() {
  const session = useSession()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  if (!session?.support?.active || !session.tenant) return null
  const back = async (tenantId: string): Promise<void> => {
    setBusy(true)
    try {
      await selectTenant(tenantId)
      void navigate('/')
    } catch (error) {
      toastError(error)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div
      role="region"
      aria-label="Support-Ansicht"
      data-testid="support-banner"
      data-print="hide"
      className={cn(
        'flex shrink-0 flex-wrap items-start gap-x-3 gap-y-1.5 border-b px-4 py-2.5 max-md:px-3 max-md:py-2',
        TONE_SUBTLE.warning,
        'text-fg',
      )}
    >
      <Headset className="mt-0.5 size-4 shrink-0 text-warning-fg" strokeWidth={1.75} aria-hidden />
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-body font-medium text-fg touch:text-touch" data-testid="support-banner-title">
          {supportBannerTitle(session.tenant.name)}
        </p>
        {/* am Telefon nur der Satz und die Handlungen */}
        <p className="text-dense text-fg-muted max-md:hidden">
          Sie haben hier alle Rechte. Was Sie tun, geschieht unter Ihrem Namen und steht als Support-Zugang im
          Audit-Protokoll des Betriebs.
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1 max-md:ml-7">
        <BackToOwn memberships={session.memberships} onSelect={(tenantId) => void back(tenantId)} busy={busy} />
        <Button
          size="sm"
          variant="ghost"
          icon={LogOut}
          onClick={() => void signOut()}
          data-testid="support-banner-logout"
        >
          Abmelden
        </Button>
      </div>
    </div>
  )
}
